import _ from 'lodash';
import React, { useState, useMemo, useCallback } from 'react';
import { Checkbox, Confirm } from 'semantic-ui-react';
import styled from 'styled-components';
import { Table, Header } from 'semantic-ui-react';
import PropTypes from 'prop-types';

const MainDiv = styled.div`
  margin-top: 20px;
  margin: auto;
`;

const SubHeader = styled.div`
  display: flex;
`;

const SubHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CheckboxHeader = styled(Checkbox)`
  width: 150px;
  display: inline-block;
  margin-right: 20px;
  margin-bottom: 10px;
  font-weight: bold;
`;

const CheckboxAll = styled(Checkbox)`
  font-weight: bold;
  float: right;
  margin-bottom: 20px;
`;

const Item = styled.span`
  margin-right: 20px;
  display: inline-block;
  width: 220px;
  margin-bottom: 10px;
`;

const NoBorderTable = styled(Table)`
  border: none !important;
`;

const Permissions = ({
  header,
  permissions,
  permissionsStructure,
  permissionsWithHeaderStructure,
  onChange,
  name,
  dataWithHeader,
  disabled,
  excludeFromSelectAll,
  confirmHeaders,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');

  const permissionsWithHeaderStructureEntries = useMemo(() => {
    if (!dataWithHeader) return null;
    return Object.entries(permissionsWithHeaderStructure);
  }, [dataWithHeader, permissionsWithHeaderStructure]);

  // Helper function to find which header a permission belongs to
  const getPermissionHeader = useCallback((permission) => {
    if (!dataWithHeader) return null;
    const entry = permissionsWithHeaderStructureEntries?.find(([key, value]) =>
      value.some((x) => x.prop === permission)
    );
    return entry ? entry[0] : null;
  }, [dataWithHeader, permissionsWithHeaderStructureEntries]);

  const onPermissionsChange = (value) => {
    onChange(null, {
      name: name,
      value: value,
    });
  };

  const changeCheck = (checked, permission) => {
    if (checked) {
      const addPermissionToList = () => {
        onPermissionsChange([...permissions, permission]);
      };

      // Check if this permission requires confirmation
      const permissionHeader = getPermissionHeader(permission);
      if (permissionHeader && confirmHeaders[permissionHeader]) {
        // Show confirmation dialog
        setConfirmMessage(confirmHeaders[permissionHeader]);
        setPendingAction(() => addPermissionToList);
        setConfirmOpen(true);
      } else {
        //if is checked adds the permission from the list
        addPermissionToList();
      }
    } else {
      //if is not checked removes the permission to the list (no confirmation needed)
      onPermissionsChange(permissions.filter((p) => p !== permission));
    }
  };

  const changeHeaderCheck = (checked, childPermissions) => {
    const childs = childPermissions.map((x) => x.prop);
    if (checked) {
      const addAllChildPermissions = () => {
        const tempPermissions = permissions.filter((p) => !childs.includes(p));
        onPermissionsChange([...tempPermissions, ...childs]);
      };

      // Check if this header requires confirmation
      const headerName = permissionsWithHeaderStructureEntries?.find(([key, value]) =>
        value === childPermissions
      )?.[0];

      if (headerName && confirmHeaders[headerName]) {
        // Show confirmation dialog
        setConfirmMessage(confirmHeaders[headerName]);
        setPendingAction(() => addAllChildPermissions);
        setConfirmOpen(true);
      } else {
        //if is checked adds the permission from the list
        addAllChildPermissions();
      }
    } else {
      //if is not checked removes the permission to the list (no confirmation needed)
      onPermissionsChange(permissions.filter((p) => !childs.includes(p)));
    }
  };

  const selectAll = (e) => {
    // Get all permissions that belong to excluded headers
    const excludedPermissions = dataWithHeader
      ? _.flatten(
          permissionsWithHeaderStructureEntries
            ?.filter(([key]) => excludeFromSelectAll.includes(key))
            .map(([key, value]) => value.map((x) => x.prop))
        )
      : [];

    if (e) {
      // Selecting all
      let allPermissions;
      if (dataWithHeader) {
        allPermissions = _.flatten(
          permissionsWithHeaderStructureEntries?.map(([key, value]) =>
            value.map((x) => x.prop),
          ),
        );
      } else {
        allPermissions = _.flatten(permissionsStructure?.map((x) => x.prop));
      }

      // Filter out excluded permissions from the "select all" list
      const permissionsToSelect = allPermissions.filter(
        (p) => !excludedPermissions.includes(p)
      );

      // Preserve any already-selected excluded permissions
      const currentlySelectedExcluded = permissions?.filter((p) =>
        excludedPermissions.includes(p)
      ) || [];

      onPermissionsChange([...permissionsToSelect, ...currentlySelectedExcluded]);
    } else {
      // Deselecting all - deselect everything including excluded permissions
      onPermissionsChange([]);
    }
  };

  const isChecked = (prop) => {
    if (permissions === null) {
      return true;
    }
    return permissions?.includes(prop);
  };

  const isSubArray = (currentPermissions, sub) =>
    sub.every((elem) => currentPermissions?.includes(elem));

  const isHeaderIndeterminate = (prop) => {
    const childProps = prop.map((x) => x.prop);
    const some = permissions?.some((permision) =>
      childProps.includes(permision),
    );
    const all = isSubArray(permissions, childProps);

    if (all || permissions === null) {
      return false;
    }
    return some;
  };

  const isHeaderChecked = (prop) => {
    const childProps = prop.map((x) => x.prop);
    const all = isSubArray(permissions, childProps);

    return all || permissions === null;
  };

  const allSelected = () => {
    let selectedOnes = [];
    if (dataWithHeader) {
      // Only check permissions from non-excluded headers
      selectedOnes = _.flatten(
        permissionsWithHeaderStructureEntries
          ?.filter(([key]) => !excludeFromSelectAll.includes(key))
          .map(([key, value]) =>
            value.map((x) => isChecked(x.prop)),
          ),
      );
    } else {
      selectedOnes = _.flatten(
        permissionsStructure?.map((x) => isChecked(x.prop)),
      );
    }
    return !selectedOnes.includes(false);
  };

  return (
    <MainDiv>
      <SubHeaderContainer>
        <Header as="h2">{header}</Header>
        {(permissionsStructure?.length > 0 ||
          permissionsWithHeaderStructureEntries?.length > 0) && (
          <CheckboxAll
            label={'Select / Deselect All'}
            checked={allSelected()}
            onChange={(e, { checked }) => selectAll(checked)}
            disabled={disabled}
          />
        )}
      </SubHeaderContainer>
      <NoBorderTable className="ui very basic collapsing celled table">
        <Table.Body>
          {dataWithHeader &&
            permissionsWithHeaderStructureEntries?.map(([key, value]) => {
              return (
                <Table.Row key={key}>
                  <Table.Cell>
                    <CheckboxHeader
                      label={key}
                      indeterminate={isHeaderIndeterminate(value)}
                      checked={isHeaderChecked(value)}
                      onChange={(e, { checked }) =>
                        changeHeaderCheck(checked, value)
                      }
                      disabled={disabled}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <span>
                      {value.map((x) => (
                        <Item key={key + '_' + x.prop}>
                          <Checkbox
                            label={x.text}
                            checked={isChecked(x.prop)}
                            onChange={(e, { checked }) =>
                              changeCheck(checked, x.prop)
                            }
                            disabled={disabled}
                          />
                        </Item>
                      ))}
                    </span>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          {!dataWithHeader && (
            <Table.Row>
              <Table.Cell>
                {permissionsStructure.map((x) => (
                  <Item key={x.prop}>
                    <Checkbox
                      label={x.text}
                      checked={isChecked(x.prop)}
                      onChange={(e, { checked }) =>
                        changeCheck(checked, x.prop)
                      }
                      disabled={disabled}
                    />
                  </Item>
                ))}
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </NoBorderTable>
      <Confirm
        open={confirmOpen}
        content={confirmMessage}
        onCancel={() => {
          setConfirmOpen(false);
          setPendingAction(null);
          setConfirmMessage('');
        }}
        onConfirm={() => {
          if (pendingAction) {
            pendingAction();
          }
          setConfirmOpen(false);
          setPendingAction(null);
          setConfirmMessage('');
        }}
      />
    </MainDiv>
  );
};

export default Permissions;

Permissions.defaultProps = {
  header: '',
  permissionsStructure: [],
  permissionsWithHeaderStructure: {},
  name: '',
  dataWithHeader: false,
  permissions: [],
  disabled: false,
  excludeFromSelectAll: [],
  confirmHeaders: {},
};

Permissions.propTypes = {
  header: PropTypes.string,
  permissionsWithHeaderStructure: PropTypes.object,
  permissionsStructure: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      prop: PropTypes.string,
    }),
  ),
  permissions: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  name: PropTypes.string,
  dataWithHeader: PropTypes.bool,
  disabled: PropTypes.bool,
  excludeFromSelectAll: PropTypes.arrayOf(PropTypes.string),
  confirmHeaders: PropTypes.object,
};
